export function pathsAreEqual(pathA, pathB) {
  if (pathA.valid !== pathB.valid) return false;
  if (pathA.absolute) {
    if (!pathB.absolute) return false;
    const aPA = pathA;
    const aPB = pathB;
    if (aPA.path.length !== aPB.path.length) return false;
    if (aPA.path.some((x, i) => x !== aPB.path[i])) return false;
  } else if (pathA.relative) {
    if (!pathB.relative) return false;
    const rPA = pathA;
    const rPB = pathB;
    if (rPA.ancestor !== rPB.ancestor) return false;
    if (rPA.path.length !== rPB.path.length) return false;
    if (rPA.path.some((x, i) => x !== rPB.path[i])) return false;
  } else {
    return false;
  }

  return true;
}

// Other
export function invertMap(map, context) {
  if (!Array.isArray(map)) {
    map = Object.entries(map);
  }

  return map
    .map(mapping => {
      if (!Array.isArray(mapping)) return null;
      const [key, value] = mapping;
      const pv = parseMapKey(value);
      if (!pv) return null;

      if (pv.type === 'array') {
        const imap = invertMap(pv.map);
        return [
          pv.array,
          {
            array: key,
            map: imap,
            initialObject: mapObject(pv.initialObject, pv.map, undefined, context),
          },
        ];
      }

      return [value, key];
    })
    .filter(x => !!x);
}


export function interpolate(text, scope) {
  const fn = typeof scope === 'function' ? (
    (...m) => scope(m[2] || m[3]) || ''
  ) : (
    (...m) => getObject(scope, m[2] || m[3]) || ''
  );

  return text.replace(/\$(([a-zA-Z]+)|{([^}]+)})/g, fn);
}


export function interpolateMap(map, scope) {
  map = Array.isArray(map) ? map : Object.entries(map);
  return map.map((kv) =>
    kv.map((k) => {
      const parsed = parseMapKey(k);
      switch (parsed?.type) {
        case 'path':
          return { type: 'path', value: interpolate(parsed.value, scope) };
        case 'const':
          return parsed;
        case 'array':
          return {
            type: 'array',
            array: interpolate(parsed.array, scope),
            map: interpolateMap(parsed.map, scope),
            initialObject: parsed.initialObject,
          };
        default:
          return parsed;
      }
    })
  ).filter(([k, v]) => k !== null && v !== null);
}

/**
 * Creates an object by mapping the fields of a form object onto an entity schema,
 * using the object map derived from map:field parameters in the form schema.
 *
 * Implements the effects of various map:field options, such as:
 *
 * Example "map:field" value:
 * ```
 * {
 *     "default": {
 *         "context": "auth.organizationalUnits[0].id"
 *     },
 *     "path": "data.orgUnitSelector",
 *     "dataType": "number"
 * }
 * ```
 *
 * @param {Object} object - The form object to map fields for.
 * @param {Object} map - The object map. Derived by `parseObjectMapFromSchema`, and found in parsed form definitions.
 * @param {Object} [initialObject]
 * @param {any} [context] - Optional context.
 * @param {Object} parentFrame - a mapping frame from a parent map object operation.
 * @returns {Object} A new object populated by the mapped fields.
 */
export function mapObject(object, map, initialObject, context, parentFrame) {
  if (!Array.isArray(map)) {
    map = Object.entries(map);
  }

  let result = initialObject || {};
  let resultType;

  const mappings = map.reduce((_, mapping) => {
    const [key, value] = mapping || [];
    const _pk = parseMapKey(key);
    const pv = parseMapKey(value);
    if (!_pk || !pv) return _;
    let pk = _pk;

    while (
      pk.type === 'default'
      || pk.type === 'writeonly'
      || pk.type === 'readFrom'
    ) pk = pk.path;

    if (pk.type !== 'path' && pk.type !== 'default') {
      // only perform assign for path keys
    } else {
      const depth = (pk.value || '').split(/\.|\[/).length + (pk.value !== '');
      _.push({ depth, pk, pv });
    }

    return _;
  }, []);

  mappings.sort(({ depth: depthA }, { depth: depthB }) => depthA - depthB);
  const parentKeyPath = parentFrame?.keyPath ?? '';
  const delim = parentKeyPath ? '.' : '';

  mappings.forEach(({ pk, pv }) => {
    const mappingFrame = {
      parent: parentFrame,
      pk,
      keyPath: `${parentKeyPath}${delim}${pk.value}`,
      object,
      initialObject: result,
      context,
      map: mappings
    };
    let mappedValue = resolvePath(pv, mappingFrame);

    const { dataType = null } = pv;

    if (mappedValue !== null && mappedValue !== undefined) {
      if (dataType) {
        mappedValue = parseDataType(dataType, mappedValue);
      }

      if (pk.value === '') {
        result = mappedValue;
        resultType = undefined;
      } else {
        if (resultType === undefined) {
          resultType = typeof result;
          if (!resultType || resultType !== 'object' || Array.isArray(result)) result = {};
        }
        setObject(result, pk.value, mappedValue);
      }
    }
  });

  return result;
}

function parseDataType(
  type,
  value
) {
  if (type === 'number') {
    return Number(value);
  }
  return value;
};

export const CUSTOM_PATHS = {};

export function resolvePath(
  pv,
  mappingFrame
) {
  const {pk, object, initialObject, context} = mappingFrame;
  let result = null;
  switch (pv.type) {
    case 'path':
      result = getObject(object, pv.value);
      break;
    case 'default':
      result = resolvePath(pv.path, mappingFrame);
      if (!result) {
        result = resolvePath(pv.default, mappingFrame);
      }
      break;
    case 'interpolate':
      result = interpolate(pv.value, { object, initialObject, context });
      break;
    case 'context':
      result = getObject(context, pv.value);
      break;
    case 'writeonly':
      result = null;
      break;
    case 'readFrom':
      result = resolvePath(pv.readFrom, mappingFrame);
      break;
    case 'readonly':
      result = resolvePath(pv.value, mappingFrame);
      break;
    case 'array':
      {
        let array = getObject(object, pv.array);
        if (array === undefined) {
          array = [];
        } else if (!Array.isArray(array)) {
          array = [array];
        }
        let ioArray = getObject(initialObject, pk.value) || [];
        if (ioArray === undefined) {
          ioArray = [];
        } else if (!Array.isArray(ioArray)) {
          ioArray = [ioArray];
        }

        result = array
          .map((item, idx) => {
            const itemMappingFrame = {
              parent: mappingFrame,
              pk,
              keyPath: `${mappingFrame?.keyPath}.${idx}`,
              object,
              initialObject: ioArray[idx],
              context,
              map: mappingFrame?.mappings
            };
            return mapObject(item, pv.map, ioArray[idx], context, itemMappingFrame)
          }).filter((item) => !isEmptyObject(item));
        if (result.length === 0) {
          result = [];
        }
      }
      break;
    default:
      if (CUSTOM_PATHS[pv.type]) {
        result = CUSTOM_PATHS[pv.type].resolve(pv, mappingFrame);
      } else {
        result = pv.value;
      }
      break;
  }
  return result;
}


export function parseMapKey(mapKey) {
  if (typeof mapKey === 'string') {
    return { type: 'path', value: mapKey };
  }
  if (!mapKey) return null;

  const { dataType } = mapKey
  const _mapKey = mapKey;

  if (_mapKey.const) {
    const constMapKey = mapKey;
    return { type: 'const', value: constMapKey.const, dataType };
  } else if (_mapKey.interpolate) {
    const interpolateMapKey = mapKey;
    return { type: 'interpolate', value: interpolateMapKey.interpolate, dataType };
  } else if (_mapKey.context) {
    const contextMapKey = mapKey;
    return { type: 'context', value: contextMapKey.context, dataType };
  } else if (_mapKey.readFrom) {
    const readFromMapKey = mapKey;
    return {
      type: 'readFrom',
      readFrom: parseMapKey(readFromMapKey.readFrom),
      path: parseMapKey(readFromMapKey.path),
      dataType,
    };
  } else if (_mapKey.readonly) {
    const readonlyMapKey = mapKey;
    return { type: 'readonly', value: parseMapKey(readonlyMapKey.readonly), dataType };
  } else if (_mapKey.writeonly) {
    const writeonlyMapKey = mapKey;
    return { type: 'writeonly', path: parseMapKey(writeonlyMapKey.writeonly), dataType };
  } else if (_mapKey.default) {
    const defaultMapKey = mapKey;
    return {
      type: 'default',
      default: parseMapKey(defaultMapKey.default),
      path: parseMapKey(defaultMapKey.path),
      dataType,
    };
  } else if (_mapKey.array && _mapKey.map) {
    const arrayMapKey = mapKey;
    return {
      type: 'array',
      array: arrayMapKey.array,
      map: arrayMapKey.map,
      initialObject: arrayMapKey.initialObject,
      dataType,
    };
  } else {
    const customMapKey = mapKey;
    const customType = Object.keys(customMapKey).find(type => CUSTOM_PATHS[type]);

    if (customType) {
      const parsedKey = {...CUSTOM_PATHS[customType].parse(customMapKey)};
      parsedKey.type = customType;
      return parsedKey;
    } else if (customMapKey.type) {
      return customMapKey
    }
  }

  return null;
}


export function parsePath(path) {
  if (path === '') return [];
  if (typeof path === "object" && "type" in path && path.type) {
    if (path.type === 'path') {
      path = path.value;
    } else {
      throw new Error('given path key is not a path key.');
    }
  }

  // if (typeof path.split !== 'function') {
  //   console.log('parsePath', path);
  // }

  const parsed = `${path}`.split('.').reduce((_, component) => {
    try {
      component.split('[').forEach((item, index) => {
        const attr = item.split(']')[0];
        const accessType = index ? 'array' : 'object';
        _.push({ accessType, attr });
      });
      return _;
    } catch (err) {
      console.log('ERROR WITH SPLIT', err);
      throw err;
    }
  }, []);

  parsed.forEach((component, index) => {
    component.nextAccessType = (parsed[index + 1] || {}).accessType;
  });

  return parsed;
}


export function parseRelPath(relpath) {
  if (typeof relpath === "object" && "valid" in relpath) {
    return relpath;
  }
  const m = /^((\$\.)|(\.+))?(\w+(\.\w+)*)?$/.exec(relpath);
  if (m) {
    const p2 = m[4] ? m[4].split('.') : [];
    if (m[2]) {
      return { valid: true, absolute: true, path: p2 };
    } else {
      return {
        valid: true,
        relative: true,
        path: p2,
        ancestor: m[3] ? m[3].length - 1 : 0,
      };
    }
  } else {
    return { valid: false };
  }
}


export function concatenatePaths(firstPath, ...relpaths) {
  if (firstPath === undefined && !relpaths.length) {
    return '';
  }

  const first = (firstPath ?? '').split('.');

  return (relpaths || [])
    .reduce((_, relpath) => {
      if (relpath === undefined || relpath === null) return _;
      const parsed = parseRelPath(relpath);
      if (parsed.valid) {
        if (parsed.absolute) return parsed.path;
        let i = parsed.ancestor;
        while (i > 0 && _.length > 0) {
          _.pop();
          i -= 1;
        }
        _.push(...parsed.path);
      }
      return _;
    }, first)
    .join('.');
}

export function splitPath(path) {
  const components = path.split('.');
  const leaf = components.pop();
  return [components.length ? components.join('.') : null, leaf];
}


export function getObject(object, path) {
  const components = parsePath(path);

  while (components.length) {
    const current = components.shift();
    let { attr } = current;
    const { accessType } = current;
    if (object !== undefined && object !== null) {
      const indexAttr = parseInt(attr, 10);
      if (accessType === 'array' && indexAttr < 0) {
        attr = object.length + indexAttr;
      }
      object = object[attr];
    }
  }

  return object;
}

export function setObject(object, path, value) {
  if (path === '') {
    Object.assign(object, value);
    return;
  }
  const components = parsePath(path);
  const lastComponent = components.pop();

  if (!lastComponent) { return; } // <-- edge case, parsed path is []

  while (components.length) {
    const { attr, nextAccessType } = components.shift();
    let nextObject = object[attr];

    if (nextObject === undefined || nextObject === null) {
      if (nextAccessType === 'array') {
        nextObject = [];
        object[attr] = nextObject;
      } else {
        // if(nextAccessType === 'object'){
        nextObject = {};
        object[attr] = nextObject;
      }
    } else if (nextAccessType === 'array' && !Array.isArray(nextObject)) {
      nextObject = [];
      object[attr] = nextObject;
    } else if (nextAccessType === 'object' && typeof nextObject !== 'object') {
      nextObject = {};
      object[attr] = nextObject;
    }

    object = nextObject;
  }

  const { attr } = lastComponent;
  object[attr] = value;
}

function isObject(value) {
  return value !== null && !Array.isArray(value) && typeof value === 'object';
}

function isEmptyObject(value) {
  return isObject(value) && Object.keys(value).length === 0;
}

export function deleteObject(object, path) {
  const components = parsePath(path);
  const lastComponent = components.pop();

  if (!lastComponent) { return; } // <-- edge case, parsed path is []

  while (components.length) {
    if (object === undefined || object === null) return;
    const { attr } = components.shift();
    object = object[attr];
  }

  if (object) {
    const { attr } = lastComponent;
    delete object[attr];
  }
}
